'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Search,
  Eye,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Calendar,
  Download,
  Briefcase,
  Clock,
  X,
  MessageSquare,
  User,
  Loader2,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


type JobStatus = 'posted' | 'assigned' | 'inprogress' | 'completed' | 'cancelled' | 'disputed';

interface Job {
  id: string;
  title: string;
  customer_name: string;
  artisan_name: string;
  category: string;
  city: string;
  posted_at: string;
  completed_at: string | null;
  value: number;
  status: JobStatus;
  has_dispute: boolean;
  description: string;
}

interface TimelineEvent {
  id: string;
  event: string;
  event_by: string;
  occurred_at: string;
}

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'posted', label: 'Posted' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'inprogress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'disputed', label: 'Disputed' },
];

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB');
}

export default function JobsOrdersContent() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [cityFilter, setCityFilter] = useState('All Cities');
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [jobTimeline, setJobTimeline] = useState<TimelineEvent[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cancelModal, setCancelModal] = useState<{ open: boolean; jobId: string; title: string }>({
    open: false,
    jobId: '',
    title: '',
  });
  const [cancelLoading, setCancelLoading] = useState(false);
  const perPage = 8;

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('jobs')
      .select('*')
      .order('posted_at', { ascending: false });
    setJobs((data as Job[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    jobs.forEach((j) => { if (j.category) cats.add(j.category); });
    return ['All Categories', ...Array.from(cats).sort()];
  }, [jobs]);

  const cities = useMemo(() => {
    const citySet = new Set<string>();
    jobs.forEach((j) => { if (j.city) citySet.add(j.city); });
    return ['All Cities', ...Array.from(citySet).sort()];
  }, [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      const matchSearch =
        !search ||
        j.id?.toLowerCase().includes(search.toLowerCase()) ||
        j.title?.toLowerCase().includes(search.toLowerCase()) ||
        j.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        j.artisan_name?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || j.status === statusFilter;
      const matchCat = categoryFilter === 'All Categories' || j.category === categoryFilter;
      const matchCity = cityFilter === 'All Cities' || j.city === cityFilter;
      return matchSearch && matchStatus && matchCat && matchCity;
    });
  }, [jobs, search, statusFilter, categoryFilter, cityFilter]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    jobs.forEach((j) => { counts[j.status] = (counts[j.status] ?? 0) + 1; });
    return counts;
  }, [jobs]);

  const openDrawer = async (job: Job) => {
    setSelectedJob(job);
    setDrawerOpen(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('job_timeline')
      .select('*')
      .eq('job_id', job.id)
      .order('occurred_at', { ascending: true });
    setJobTimeline(data ?? []);
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    const supabase = createClient();
    const { error } = await supabase
      .from('jobs')
      .update({ status: 'cancelled' })
      .eq('id', cancelModal.jobId);

    if (error) {
      toast.error('Failed to cancel job. Please try again.');
    } else {
      toast.success(`Job ${cancelModal.jobId} has been cancelled`);
      await fetchJobs();
    }
    setCancelLoading(false);
    setCancelModal({ open: false, jobId: '', title: '' });
    setDrawerOpen(false);
  };

  if (loading) {
    return (
      <div className="px-6 py-6 max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`skel-stat-${i}`} className="card-elevated rounded-xl px-4 py-3 h-16 animate-pulse bg-muted" />
          ))}
        </div>
        <div className="card-elevated rounded-xl overflow-hidden">
          <div className="p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`skel-row-${i}`} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-screen-2xl mx-auto">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { status: 'posted', label: 'Posted', color: 'text-sky-600' },
          { status: 'assigned', label: 'Assigned', color: 'text-blue-600' },
          { status: 'inprogress', label: 'In Progress', color: 'text-purple-600' },
          { status: 'completed', label: 'Completed', color: 'text-emerald-600' },
          { status: 'cancelled', label: 'Cancelled', color: 'text-slate-600' },
          { status: 'disputed', label: 'Disputed', color: 'text-red-600' },
        ].map((s) => (
          <button
            key={`jstat-${s.status}`}
            onClick={() => { setStatusFilter(statusFilter === s.status ? 'all' : s.status); setPage(1); }}
            className={`card-elevated rounded-xl px-4 py-3 text-left transition-all hover:shadow-elevated ${
              statusFilter === s.status ? 'ring-2 ring-primary' : ''
            }`}
          >
            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
            <p className={`text-2xl font-700 font-tabular ${s.color}`}>
              {statusCounts[s.status] ?? 0}
            </p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card-elevated rounded-xl p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2 flex-1 min-w-48">
            <Search size={14} className="text-muted-foreground shrink-0" />
            <input
              type="text"
              placeholder="Search by job ID, title, customer, artisan..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground outline-none focus:ring-2 focus:ring-primary/20"
          >
            {statusOptions.map((o) => (
              <option key={`jstatus-${o.value}`} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
            className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground outline-none focus:ring-2 focus:ring-primary/20"
          >
            {categories.map((c) => (
              <option key={`jcat-${c}`} value={c}>{c}</option>
            ))}
          </select>

          <select
            value={cityFilter}
            onChange={(e) => { setCityFilter(e.target.value); setPage(1); }}
            className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground outline-none focus:ring-2 focus:ring-primary/20"
          >
            {cities.map((c) => (
              <option key={`jcity-${c}`} value={c}>{c}</option>
            ))}
          </select>

          <button className="btn-ghost border border-border flex items-center gap-2 ml-auto">
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card-elevated rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">Job ID</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Title</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Customer</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Artisan</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Category</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">City</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide whitespace-nowrap">Posted</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Value</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Status</th>
                <th className="w-24 px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-16 text-center">
                    <Briefcase size={32} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-600 text-foreground mb-1">No jobs found</p>
                    <p className="text-xs text-muted-foreground">Try adjusting your search or filter criteria.</p>
                  </td>
                </tr>
              ) : (
                paginated.map((job) => (
                  <tr key={job.id} className="table-row-hover transition-colors group">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-700 font-mono text-primary">{job.id}</span>
                        {job.has_dispute && (
                          <AlertTriangle size={12} className="text-red-500" title="Has active dispute" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm font-500 text-foreground max-w-[200px] truncate" title={job.title}>
                        {job.title}
                      </p>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <User size={12} className="text-muted-foreground shrink-0" />
                        <span className="text-xs text-foreground">{job.customer_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <User size={12} className="text-muted-foreground shrink-0" />
                        <span className="text-xs text-foreground">{job.artisan_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs font-500 bg-primary/8 text-primary px-2 py-0.5 rounded-full whitespace-nowrap">
                        {job.category}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                        <MapPin size={11} />
                        {job.city}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                        <Calendar size={11} />
                        {formatDate(job.posted_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-sm font-700 font-tabular text-foreground">
                        OMR {(job.value ?? 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={job.status} size="sm" />
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openDrawer(job)}
                          title="View job details"
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                        >
                          <Eye size={14} />
                        </button>
                        {(job.status === 'posted' || job.status === 'assigned' || job.status === 'inprogress') && (
                          <button
                            onClick={() => setCancelModal({ open: true, jobId: job.id, title: job.title })}
                            title="Cancel job"
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <XCircle size={14} />
                          </button>
                        )}
                        {job.has_dispute && (
                          <button
                            title="View dispute"
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          >
                            <AlertTriangle size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {Math.min((page - 1) * perPage + 1, filtered.length)}–{Math.min(page * perPage, filtered.length)} of{' '}
            <span className="font-600 text-foreground">{filtered.length}</span> jobs
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`jpage-${i + 1}`}
                onClick={() => setPage(i + 1)}
                className={`w-8 h-8 text-xs rounded-lg font-600 transition-colors ${
                  page === i + 1
                    ? 'bg-primary text-white' :'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || totalPages === 0}
              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Job detail drawer */}
      {drawerOpen && selectedJob && (
        <div
          className="fixed inset-0 z-40 bg-black/30 fade-in"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="absolute right-0 top-0 h-full w-full max-w-lg bg-card shadow-modal overflow-y-auto scrollbar-thin slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-base font-700 text-foreground">{selectedJob.id}</h2>
                <p className="text-xs text-muted-foreground">Job details</p>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="text-base font-600 text-foreground">{selectedJob.title}</h3>
                  <StatusBadge status={selectedJob.status} />
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{selectedJob.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Customer', value: selectedJob.customer_name, icon: User },
                  { label: 'Artisan', value: selectedJob.artisan_name, icon: User },
                  { label: 'Category', value: selectedJob.category, icon: Briefcase },
                  { label: 'City', value: selectedJob.city, icon: MapPin },
                  { label: 'Posted', value: formatDate(selectedJob.posted_at), icon: Calendar },
                  { label: 'Value', value: `OMR ${(selectedJob.value ?? 0).toLocaleString()}`, icon: Clock },
                ].map((info) => {
                  const Icon = info.icon;
                  return (
                    <div key={`jinfo-${info.label}`} className="bg-muted rounded-lg p-3">
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                        <Icon size={11} />
                        {info.label}
                      </p>
                      <p className="text-sm font-600 text-foreground">{info.value}</p>
                    </div>
                  );
                })}
              </div>

              {selectedJob.has_dispute && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle size={16} className="text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-600 text-red-700">Active dispute on this job</p>
                    <p className="text-xs text-red-600 mt-0.5">
                      Navigate to Dispute Management to review.
                    </p>
                  </div>
                </div>
              )}

              {/* Timeline */}
              {jobTimeline.length > 0 && (
                <div>
                  <h4 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-3">Job Timeline</h4>
                  <div className="relative">
                    <div className="absolute left-3.5 top-2 bottom-2 w-px bg-border" />
                    <div className="space-y-4">
                      {jobTimeline.map((event, idx) => (
                        <div key={event.id} className="flex items-start gap-4 pl-8 relative">
                          <div
                            className={`absolute left-2 w-3 h-3 rounded-full border-2 border-white ${
                              idx === jobTimeline.length - 1 ? 'bg-accent' : 'bg-primary'
                            }`}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-600 text-foreground">{event.event}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-muted-foreground">
                                {formatDate(event.occurred_at)}
                              </span>
                              <span className="text-xs text-muted-foreground">·</span>
                              <span className="text-xs text-muted-foreground">{event.event_by}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {(selectedJob.status === 'posted' || selectedJob.status === 'assigned' || selectedJob.status === 'inprogress') && (
                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={() => {
                      setDrawerOpen(false);
                      setCancelModal({ open: true, jobId: selectedJob.id, title: selectedJob.title });
                    }}
                    className="btn-danger flex items-center gap-2"
                  >
                    <XCircle size={14} />
                    Cancel Job
                  </button>
                  <button className="btn-ghost border border-border flex items-center gap-2">
                    <MessageSquare size={14} />
                    Message Parties
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel modal */}
      <Modal
        open={cancelModal.open}
        onClose={() => setCancelModal({ open: false, jobId: '', title: '' })}
        title="Cancel Job"
        size="sm"
      >
        <p className="text-sm text-muted-foreground mb-2">
          Cancel <span className="font-600 text-foreground">{cancelModal.jobId}</span>?
        </p>
        <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
          &ldquo;{cancelModal.title}&rdquo; — Both the customer and artisan will be notified.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setCancelModal({ open: false, jobId: '', title: '' })}
            className="btn-ghost border border-border"
          >
            Keep Job
          </button>
          <button
            onClick={handleCancel}
            disabled={cancelLoading}
            className="btn-danger flex items-center gap-2 disabled:opacity-70"
          >
            {cancelLoading && <Loader2 size={14} className="animate-spin" />}
            <XCircle size={14} />
            Cancel Job
          </button>
        </div>
      </Modal>
    </div>
  );
}